import React, { useContext, useEffect } from 'react'
import { addDays, isToday } from 'date-fns'
import { makeStyles } from '@material-ui/core/styles'
import { Typography } from '@material-ui/core'
import clsx from 'clsx'
import { Controller } from 'react-hook-form'

import { context, setAnytimeToday, useSelectors } from '../../store'
import { fields, slots } from '../../store/constants'
import { useLivePrice } from '../../lib/use-live-price'
import { SAME_DAY_WINDOW_LABEL, sameDayOpen } from '../../lib/same-day'
import { radioGroupKeyDown } from '../../lib/radio-keys'
import { isOff, slotState, stateNote } from '../../lib/availability'
import { styles } from './styles'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)

// Same-day cutoff (12pm London, lib/same-day.js): from then the "Today"
// option is not shown at all, but tomorrow stays available.

// The slots a customer can pick, in display order. "Anytime today" is
// handled separately (same-day surcharge, cutoff).
const SLOT_OPTIONS = [
  // "to", not a dash: no dashes in customer copy (booking-screen-trim).
  { value: slots.allday, label: 'Anytime', sub: '8am to 6pm' },
  { value: slots.slot1, label: 'Morning', sub: '8am to 12pm' },
  { value: slots.slot2, label: 'Midday', sub: '10am to 2pm' },
  { value: slots.slot3, label: 'Afternoon', sub: '2pm to 6pm' }
]

// Inline slot buttons (since 2026-09-17). Replaced a read-only field that
// opened a dialog — one tap now, nothing to close. "Anytime 8am–6pm" is
// pre-selected so the field is never a blocker; onEngage lets the Details
// step record field_engaged when the customer actively changes it.
// `availability`: the days for the car's district (lib/availability.js, Form
// v3): an unavailable slot is greyed, a limited one shows its reason.
export const TimeSlots = ({ form, required = false, onEngage, todayInDate = false, availability }) => {
  const classes = useStyles()
  const { state, dispatch } = useContext(context)
  const { isSameDaySelected, date } = useSelectors()
  const priceFor = useLivePrice()
  const sameDayPrice = priceFor('Extra for same day')

  const current = form.watch(fields.timeSlot)
  const isAnytimeTodayDisabled = (date && !isToday(date)) || !sameDayOpen()

  // Default: the first option, unless the date is "today" (BookDate sets the
  // same-day slot itself) or a slot is already chosen.
  useEffect(() => {
    if (current) return
    if (isSameDaySelected) return
    form.setValue(fields.timeSlot, slots.allday, { shouldValidate: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, isSameDaySelected])

  const choose = newTimeSlot => () => {
    onEngage && onEngage(fields.timeSlot)
    const now = new Date()
    if (state.date && now.toDateString() === state.date.toDateString()) {
      // A normal slot while the date is still today → bump to tomorrow
      // (always allowed; the cutoff only governs same-day).
      form.setValue(fields.date, addDays(now, 1), { shouldValidate: true })
    }
    dispatch(setAnytimeToday(false))
    form.setValue(fields.timeSlot, newTimeSlot, { shouldValidate: true })
  }

  const chooseAnytimeToday = () => {
    onEngage && onEngage(fields.timeSlot)
    dispatch(setAnytimeToday(true))
    form.setValue(fields.timeSlot, slots.today, { shouldValidate: true })
    form.setValue(fields.date, new Date(), { shouldValidate: true })
  }

  return (
    <Controller
      name={fields.timeSlot}
      control={form.control}
      rules={{ required: required && 'Pick a time' }}
      render={({ field: { value, ...field }, fieldState: { error } }) => (
        <>
          <Typography className={classes.title}>Time</Typography>
          <input {...field} value={value || ''} hidden />
          <div className={classes.group} role="radiogroup" aria-label="Time slot" aria-invalid={!!error} onKeyDown={radioGroupKeyDown}>
            {todayInDate && value === slots.today ? (
              // Today picked in the day chips: one fixed window, the one the
              // same-day washer keeps (ruled 8 Oct 2026).
              <button type="button" role="radio" aria-checked className={clsx(classes.option, classes.optionSelected)}>
                <span className={classes.optionLabel}>Today</span>
                <span className={classes.optionSub}>{SAME_DAY_WINDOW_LABEL}</span>
              </button>
            ) : SLOT_OPTIONS.map(opt => {
              const state = slotState(availability, date, opt.value)
              return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                disabled={isOff(state)}
                aria-checked={value === opt.value}
                // Not disabled while today is picked: a tap here is how a
                // customer leaves same-day (choose() moves the date to
                // tomorrow). Disabled, two phone visitors tapped Morning and
                // Midday after picking today and nothing happened
                // (docs/MOBILE_BOOK_DROP_2026-10-07.md).
                className={clsx(classes.option, value === opt.value && classes.optionSelected, error && classes.optionInvalid)}
                onClick={choose(opt.value)}>
                <span className={classes.optionLabel}>{opt.label}</span>
                <span className={classes.optionSub}>{stateNote(state) || opt.sub}</span>
              </button>
              )
            })}
            {!todayInDate && !isAnytimeTodayDisabled && (
              <button
                type="button"
                role="radio"
                aria-checked={value === slots.today}
                className={clsx(classes.option, classes.optionToday, value === slots.today && classes.optionSelected)}
                onClick={chooseAnytimeToday}>
                <span className={classes.optionLabel}>Today</span>
                <span className={classes.optionSub}>{sameDayPrice ? `+${sameDayPrice}` : 'extra charge'}</span>
              </button>
            )}
          </div>
        </>
      )}
    />
  )
}
